import { useState, useEffect } from 'react';
import { Tablet } from 'lucide-react';
import { serieMatchCourt, serieMatchDate, serieMatchStart, serieMatchEnd } from '../../engine';
import { KMCourtCard, KMScoreModal } from '../km';
import { SeriePadBanner } from '../seriespill';
import { deviceFillBoxStyle } from './deviceFill';

export interface SeriespillSingleCourtPadProps {
  /** The seriespill (league) tournament — matches/pairs/config drive the pad. */
  series: any;
  /** The single court whose pad is rendered. */
  court: any;
  /** All players (resolves names/ELO in KMCourtCard + the standings banner). */
  allPlayers?: any[];
  /** Records a result (the updated match object). */
  onRecordResult?: any;
  /** Persists the in-progress (live) score without finalising: (updatedMatch). */
  onLiveScore?: any;
  /** Called when a player name is clicked. */
  onPlayerClick?: any;
  /**
   * Live clock from the parent (the pad shell's, which is also what resolved
   * which session belongs on this court). Falls back to an internal 1s tick
   * when omitted, so standalone renders and the tournament-view call sites are
   * unchanged.
   */
  now?: any;
  /** Division standings computed elsewhere — forwarded to <SeriePadBanner>. */
  standings?: any[] | null;
  /** Device kiosk mode: fill the whole tablet (forwarded to KMCourtCard + empty state). */
  fill?: boolean;
}

// Seriespill banepad for a single court. Mirrors KMSingleCourtPad: reuses
// KMCourtCard + KMScoreModal (pair-based, game/set entry). The match's
// rules (super-tiebreak, number of sets, short set) are fetched automatically
// from match.matchFormat — KMScoreModal renders the correct entry per match.
/**
 * Single-court banepad for seriespill (league play) — mirrors KMSingleCourtPad
 * (reuses KMCourtCard + KMScoreModal, pair-based game/set entry) and pins the
 * condensed division standings (SeriePadBanner) inside the pad as a footer.
 * Extracted verbatim from the monolith (legacy/App.jsx); markup/logic unchanged.
 */
export function SeriespillSingleCourtPad({
  series,
  court,
  allPlayers = [],
  onRecordResult,
  onLiveScore,
  onPlayerClick,
  now: nowProp,
  standings = null,
  fill = false,
}: SeriespillSingleCourtPadProps) {
  // The pad shell's clock when it threads one down; see CourtPadTab.
  const [ownNow, setOwnNow] = useState(() => new Date());
  useEffect(() => {
    if (nowProp) return undefined;
    const id = setInterval(() => setOwnNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [nowProp]);
  const now = nowProp ?? ownNow;
  const [scoreModalMatch, setScoreModalMatch] = useState(null);
  // Local date format (NEVER toISOString) per project rules.
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const clock = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const accent = '#14b8a6'; // serie teal (matches the SERIESPILL stripe)
  const courtMatches = (series?.matches || []).filter(
    (m) => Number(serieMatchCourt(m)) === Number(court),
  );
  const allDivisions = (series?.config?.classes || []).flatMap((cls) =>
    (cls.divisions || []).map((div) => ({ ...div, className: cls.name, classGender: cls.gender })),
  );
  // Current match = the match being played right now (by time), otherwise the next unplayed one with both pairs.
  // Court/date/time are resolved effectively (also from the reservation via bookingInfo).
  const minsOf = (t) => {
    if (!t) return 0;
    const [h, mm] = String(t).split(':').map(Number);
    return (h || 0) * 60 + (mm || 0);
  };
  const current =
    courtMatches.find(
      (m) =>
        serieMatchDate(m) === today &&
        minsOf(serieMatchStart(m)) <= nowMin &&
        minsOf(serieMatchEnd(m)) > nowMin &&
        !m.score &&
        m.team1?.pairId &&
        m.team2?.pairId,
    ) || courtMatches.find((m) => !m.score && m.team1?.pairId && m.team2?.pairId);
  const save = (updatedMatch) => {
    onRecordResult?.(updatedMatch);
    setScoreModalMatch(null);
  };
  const openScore = (m) => setScoreModalMatch(m);
  if (courtMatches.length === 0) {
    return (
      <div
        className="rounded-2xl flex items-center justify-center"
        style={{
          aspectRatio: '4 / 3',
          background: 'linear-gradient(165deg, #0a0b10 0%, #14161f 100%)',
          color: 'rgba(230,251,255,0.5)',
          ...deviceFillBoxStyle(fill),
        }}
      >
        <div className="text-center">
          <Tablet size={28} className="mx-auto mb-2 opacity-40" />
          <div className="font-mono text-[11px] uppercase tracking-wider">
            Ingen seriekamper på bane {court}
          </div>
        </div>
      </div>
    );
  }
  // The division standings render INSIDE the pad (footer) so they follow the match.
  const tableFooter = (
    <SeriePadBanner
      series={series}
      court={court}
      allPlayers={allPlayers}
      focusMatch={current}
      standings={standings}
    />
  );
  return (
    <>
      {current ? (
        <KMCourtCard
          court={court}
          fill={fill}
          matches={[current]}
          pairs={series.pairs}
          allPlayers={allPlayers}
          divisions={allDivisions}
          nowMin={nowMin}
          today={today}
          clock={clock}
          locked={false}
          formatType="seriespill"
          footer={tableFooter}
          onSaveScore={save}
          onScoreChange={onLiveScore}
          onClickScore={openScore}
          onPlayerClick={onPlayerClick}
        />
      ) : (
        <div
          className="rounded-2xl overflow-hidden flex flex-col"
          style={{
            background: 'linear-gradient(165deg, #0a0b10 0%, #14161f 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <div
            className="flex items-center justify-center"
            style={{ minHeight: 120, color: 'rgba(230,251,255,0.55)' }}
          >
            <div className="text-center px-6">
              <div
                className="font-mono text-[11px] uppercase tracking-wider mb-1"
                style={{ color: '#5eead4' }}
              >
                Ingen kamp akkurat nå
              </div>
              <div className="font-mono text-[10px]" style={{ color: 'rgba(230,251,255,0.4)' }}>
                Alle seriekamper på bane {court} er ferdigspilt.
              </div>
            </div>
          </div>
          {tableFooter}
        </div>
      )}
      {scoreModalMatch && (
        <KMScoreModal
          match={scoreModalMatch}
          pairs={(series.pairs || []).filter((p) => p.divisionId === scoreModalMatch.divisionId)}
          onClose={() => setScoreModalMatch(null)}
          onSave={save}
          accentColor={accent}
        />
      )}
    </>
  );
}
